// src/App.js
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { LoadingScreen } from './components/ui/LoadingScreen';

import { LoginPage } from './pages/auth/LoginPage';
import { MFASetupPage } from './pages/auth/MFASetupPage';
import { UnauthorizedPage, InactivePage } from './pages/auth/UnauthorizedPage';
import { getAllModules } from './config/modules';

const TemplateBuilderPage = lazy(() => import('./pages/admin/TemplateBuilderPage'));
const KioskPage = lazy(() => import('./pages/kiosk/KioskPage'));
const CheckoutPage      = lazy(() => import('./pages/checkout/CheckoutPage'));
const VisitResponsePage = lazy(() => import('./pages/visit-response/VisitResponsePage'));
const BatchRecordPage = lazy(() => import('./pages/ebr/BatchRecordPage'));
const PrintableBatchRecord = lazy(() => import('./pages/ebr/PrintableBatchRecord'));

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes — no auth required */}
            <Route path="/kiosk" element={<KioskPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/visit-response" element={<VisitResponsePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/mfa-setup" element={<MFASetupPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/inactive" element={<InactivePage />} />

            {/* Printable batch record (outside AppLayout for clean print) */}
            <Route
              path="/ebr/:recordId/print"
              element={
                <ProtectedRoute>
                  <PrintableBatchRecord />
                </ProtectedRoute>
              }
            />

            <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />

              {getAllModules().map((module) => (
                <Route
                  key={module.slug}
                  path={module.path.replace('/', '')}
                  element={
                    <ProtectedRoute requiredModule={module.slug}>
                      <module.component />
                    </ProtectedRoute>
                  }
                />
              ))}

              {/* Template builder */}
              <Route
                path="admin/templates/:templateId"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <TemplateBuilderPage />
                  </ProtectedRoute>
                }
              />

              {/* Batch record view */}
              <Route
                path="ebr/:recordId"
                element={
                  <ProtectedRoute>
                    <BatchRecordPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
